import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  Input,
  Image,
  Select,
  Modal,
  Tag,
  Typography,
} from "antd";
import { callApi } from "../../api/apiCaller";
import routes from "../../api/routes";
import Loader from "../../components/loader/loader";
import { GreenNotify, RedNotify } from "../../helper/helper";
import { isValidEmail } from "../../utils/isValidEmail";
import ImageUploader from "../../components/imageUploader/imageUploader";

const { Option } = Select;
const { Paragraph } = Typography;

const TrainerList = () => {
  const [users, setUsers] = useState([]);
  const [isloading, setIsLoading] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredRole, setFilteredRole] = useState("trainer");
  const [selectedRole, setSelectedRole] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState([]);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // NEW STATE FOR VERIFICATION MODAL
  const [isVerifyModalVisible, setIsVerifyModalVisible] = useState(false);
  const [trainerToVerify, setTrainerToVerify] = useState(null);

  const [isEditProfileModalVisible, setIsEditProfileModalVisible] =
    useState(false);
  const [editProfileData, setEditProfileData] = useState({
    userId: "",
    name: "",
    age: null,
    gender: "other",
    profilePicture: "",
  });

  const [editUserData, setEditUserData] = useState({
    _id: "",
    email: "",
  });

  const [newUserData, setNewUserData] = useState({
    email: "",
    role: "mother",
  });

  const getAllUser = async (role) => {
    const url = `${routes.getAllUsers}?role=trainer`;

    await callApi(
      "GET",
      url,
      null,
      setIsLoading,
      (res) => {
        setUsers(res.users || []);
      },
      (err) => console.log("Fetch Error", err)
    );
  };

  useEffect(() => {
    getAllUser(filteredRole);
  }, [filteredRole]);

  const handleRoleChange = (value) => setSelectedRole(value || undefined);
  const handleModalClose = () => setIsModalVisible(false);

  const openEditModal = (user) => {
    setEditUserData({
      _id: user.key,
      email: user.email,
    });
    setIsEditModalVisible(true);
  };

  const handleCreateUser = async () => {
    setIsCreatingUser(true);
    if (!newUserData.email || !selectedRole) {
      setIsCreatingUser(false);
      return RedNotify("Please provide both email and role.");
    }

    if (!isValidEmail(newUserData.email)) {
      setIsCreatingUser(false);
      return RedNotify("Please enter a valid email address.");
    }

    if (password && password.length < 6) {
      setIsCreatingUser(false);
      return RedNotify("Password must be at least 6 characters long.");
    }

    if (password && password !== confirmPassword) {
      setIsCreatingUser(false);
      return RedNotify("Passwords do not match.");
    }

    const payload = {
      email: newUserData.email,
      role: selectedRole,
    };

    if (password) payload.password = password;

    try {
      await callApi(
        "POST",
        routes.signUp,
        payload,
        () => {},
        () => {
          setIsCreateModalVisible(false);
          setNewUserData({ email: "", role: "mother" });
          setPassword("");
          setConfirmPassword("");

          GreenNotify("User created successfully!");
          setIsCreatingUser(false);
          getAllUser(filteredRole);
        },
        (err) => {
          RedNotify(err?.message || "Failed to create user.");
          setIsCreatingUser(false);
        }
      );
    } catch (error) {
      setIsCreatingUser(false);
      RedNotify("Unexpected error occurred while creating user.");
    }
  };

  const handleEditUser = async () => {
    try {
      await callApi(
        "PUT",
        `${routes.updateUser}/${editUserData._id}`,
        editUserData,
        () => {},
        () => {
          GreenNotify("User updated successfully!");
          setIsEditModalVisible(false);
          getAllUser(filteredRole);
        },
        (err) => {
          RedNotify(err?.message || "Failed to update user.");
        }
      );
    } catch (error) {
      RedNotify("Unexpected error occurred while editing user.");
    }
  };

  const openEditProfileModal = (user) => {
    const profile = user.profile || {};
    setEditProfileData({
      userId: user.key,
      name: profile.name || "",
      age: profile.age || "",
      gender: profile.gender || "other",
      profilePicture: profile.profilePicture || "",
      profileId: profile._id || null,
    });

    setEditUserData((prev) => ({
      ...prev,
      _id: user.key,
      email: user.email,
    }));

    setIsEditProfileModalVisible(true);
  };

  const handleUpdateProfile = async () => {
    const body = {
      userId: editProfileData.userId,
      name: editProfileData.name,
      age: editProfileData.age,
      gender: editProfileData.gender,
      profilePicture: editProfileData.profilePicture,
    };

    const isEdit = Boolean(editProfileData.profileId);
    const url = isEdit ? routes.updateProfile : routes.createProfile;
    const method = isEdit ? "PUT" : "POST";

    try {
      await callApi(
        method,
        url,
        body,
        () => {},
        () => {
          GreenNotify(isEdit ? "Profile updated" : "Profile created");
          setIsEditProfileModalVisible(false);
          getAllUser(filteredRole);
        },
        (err) => {
          RedNotify(err?.message || "Profile update failed");
        }
      );
    } catch (err) {
      RedNotify("Unexpected error occurred");
    }
  };

  const handleRecoverUser = async (userId) => {
    try {
      await callApi(
        "PATCH",
        `${routes.recoverUser}/${userId}`,
        {},
        () => {},
        () => {
          GreenNotify("User recovered successfully.");
          getAllUser(filteredRole);
        },
        (err) => RedNotify(err?.message || "Failed to recover user.")
      );
    } catch (error) {
      RedNotify("Unexpected error occurred during recovery.");
    }
  };

  const handleDelete = async (index, event, userId) => {
    event.stopPropagation();
    Modal.confirm({
      title: "Are you sure you want to delete this user?",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await callApi(
            "DELETE",
            `${routes.deleteUser}/${userId}`,
            {},
            () => {},
            (res) => {
              GreenNotify(res?.message || "User deleted successfully.");
              const updatedUsers = users.filter((user) => user._id !== userId);
              setUsers(updatedUsers);
            },
            (err) => RedNotify(err?.message || "Failed to delete user.")
          );
        } catch (error) {
          RedNotify("Unexpected error occurred during deletion.");
        }
      },
    });
  };

  const blocked = async (index, event, userId, blockStatus) => {
    const updatedUsers = [...users];
    const userIndex = updatedUsers.findIndex((user) => user._id === userId);

    if (userIndex !== -1) updatedUsers[userIndex].isBlocked = blockStatus;

    try {
      await callApi(
        "PATCH",
        `${routes.blockUser}/${userId}`,
        { isBlocked: blockStatus },
        () => {},
        () => {
          GreenNotify(
            `User ${blockStatus ? "blocked" : "unblocked"} successfully!`
          );
          setUsers(updatedUsers);
        },
        () => getAllUser(filteredRole)
      );
    } catch {
      RedNotify("Unexpected error occurred while changing block status.");
    }
  };

  const getRowClassName = (record) => {
    if (record.isBlocked) return "row-blocked";
    return "";
  };

  // NEW: HANDLERS FOR THE VERIFY TRAINER BUTTON AND MODAL
  const handleVerifyTrainerClick = (trainer) => {
    setTrainerToVerify(trainer);
    setIsVerifyModalVisible(true);
  };

  const handleConfirmVerification = async () => {
    if (!trainerToVerify) return;

    const shouldVerify = !trainerToVerify.isTrainerVerified;

    try {
      await callApi(
        "PUT",
        `${routes.verifyTrainer}/${trainerToVerify.key}`,
        { verify: shouldVerify },
        () => {},
        () => {
          GreenNotify(
            `Trainer ${shouldVerify ? "verified" : "unverified"} successfully!`
          );
          setIsVerifyModalVisible(false);
          getAllUser(filteredRole); // Refresh the list
        },
        (err) =>
          RedNotify(err?.message || "Failed to update verification status.")
      );
    } catch (error) {
      RedNotify("Unexpected error occurred during verification toggle.");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      align: "center",
    },
    {
      title: "Email",
      dataIndex: "email",
      align: "center",
    },
    {
      title: "Profile Picture",
      dataIndex: "profilePicture",
      align: "center",
      render: (src) => (
        <Image
          width={50}
          height={50}
          src={src}
          alt="profile"
          style={{ borderRadius: "50%" }}
        />
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      align: "center",
      render: (role) => (
        <span style={{ textTransform: "capitalize" }}>{role}</span>
      ),
    },
    // NEW: VERIFICATION STATUS COLUMN
    {
      title: "Verification Status",
      dataIndex: "isTrainerVerified",
      align: "center",
      render: (isVerified) =>
        isVerified ? (
          <Tag color="green">Verified</Tag>
        ) : (
          <Tag color="red">Not Verified</Tag>
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      align: "center",
      render: (desc) => desc || "N/A",
    },
    {
      title: "Age",
      dataIndex: "age",
      align: "center",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      align: "center",
    },
    {
      title: "Weight",
      dataIndex: "weight",
      align: "center",
    },
    {
      title: "Height",
      dataIndex: "height",
      align: "center",
    },
    {
      title: "Chats Allowed",
      dataIndex: "allowChats",
      align: "center",
      render: (val) =>
        val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
    },
    {
      title: "Expertise",
      dataIndex: "expertise",
      align: "center",
      render: (_, record) => {
        const combined = [
          ...(Array.isArray(record.expertise) ? record.expertise : []),
          ...(Array.isArray(record.otherExpertise)
            ? record.otherExpertise
            : []),
        ];
        return combined.length > 0 ? combined.join(", ") : "N/A";
      },
    },

    {
      title: "Certificates",
      dataIndex: "certificate",
      align: "center",
      render: (certs) =>
        Array.isArray(certs) && certs.length > 0 ? (
          <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
            {certs.map((url, idx) => (
              <li key={idx}>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  View Certificate {idx + 1}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Taxation Forms",
      dataIndex: "taxationForms",
      align: "center",
      render: (forms) =>
        Array.isArray(forms) && forms.length > 0 ? (
          <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
            {forms.map((url, idx) => (
              <li key={idx}>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  View Form {idx + 1}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Bank Details",
      dataIndex: "bankDetails",
      align: "center",
      render: (bank) =>
        bank?.bankName ? (
          <div>
            <p>
              <b>{bank.bankName}</b>
            </p>
            <p>{bank.accountNumber}</p>
            <p>{bank.accountTitle}</p>
            <p>{bank.accountType}</p>
          </div>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Block User",
      dataIndex: "blockORUnblock",
      align: "center",
      render: (_, record, index) => (
        <Button
          style={{
            color: "white",
            backgroundColor: record.isBlocked ? "#52c41a" : "#f5222d",
          }}
          onClick={(e) => blocked(index, e, record.key, !record.isBlocked)}
        >
          {record.isBlocked ? "Unblock" : "Block"}
        </Button>
      ),
    },
    {
      title: "Verify / Unverify Trainer",
      dataIndex: "verifyTrainer",
      align: "center",
      render: (_, record) => {
        if (record.role === "trainer") {
          return (
            <Button
              type={record.isTrainerVerified ? "default" : "primary"}
              danger={record.isTrainerVerified}
              onClick={() => handleVerifyTrainerClick(record)}
            >
              {record.isTrainerVerified ? "Unverify Trainer" : "Verify Trainer"}
            </Button>
          );
        }
        return null;
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      align: "center",
      render: (_, record, index) => {
        if (record.isDeleted) {
          return (
            <Button
              style={{ backgroundColor: "#52c41a", color: "white" }}
              onClick={() => handleRecoverUser(record.key)}
            >
              Recover
            </Button>
          );
        }
        return (
          <Button
            style={{ backgroundColor: "#f5222d", color: "white" }}
            onClick={(e) => handleDelete(index, e, record.key)}
          >
            Delete
          </Button>
        );
      },
    },
  ];

  const data = users
    .map((item) => {
      const profile = item.profile || {};
      return {
        key: item._id,
        name: profile.name || "",
        email: item.email,
        role: item.role,
        profilePicture: profile.profilePicture || "",
        description: profile.description || "",
        age: profile.age,
        gender: profile.gender,
        weight: profile.weight
          ? `${profile.weight.value} ${profile.weight.unit}`
          : "N/A",
        height: profile.height
          ? `${profile.height.value} ${profile.height.unit}`
          : "N/A",
        expertise: profile.expertise,
        otherExpertise: profile.otherExpertise,
        certificate: profile.certificate,
        taxationForms: profile.taxationForms,
        allowChats: profile.allowChats,
        bankDetails: profile.bankDetails || {},
        isTrainerVerified: item.isTrainerVerified,
        isBlocked: item.isBlocked,
        isDeleted: item.isDeleted,
        profile,
      };
    })
    .filter((item) => {
      const nameMatch = item.name
        ?.toLowerCase()
        .includes(searchText.toLowerCase());
      const emailMatch = item.email
        ?.toLowerCase()
        .includes(searchText.toLowerCase());
      return nameMatch || emailMatch;
    });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchText]);

  return (
    <div className="admin-products-main-container">
      <div className="configure-server-roles-main-heading-container">
        <h1 style={{ marginInline: "24px", marginBlock: "12px" }}>
          Users List
        </h1>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginInline: "24px",
            marginBlock: "12px",
          }}
        >
          <Input
            placeholder="Search Name or Email"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: "30rem" }}
          />
        </div>
      </div>

      <div className="server-roles-tb-main-container">
        <Table
          rowClassName={getRowClassName}
          columns={columns}
          dataSource={data.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data.length,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50", "100"],
          }}
          className="subscriptionapi-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        title="Details"
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
      >
        <div className="modal-content-list">
          {modalContent && modalContent.length > 0 ? (
            <ul>
              {modalContent.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          ) : (
            <p>No items to display</p>
          )}
        </div>
      </Modal>

      <Modal
        title="Create New User"
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onOk={handleCreateUser}
        okText="Create"
        confirmLoading={isCreatingUser}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Input
            placeholder="Enter Email"
            value={newUserData.email}
            onChange={(e) =>
              setNewUserData({ ...newUserData, email: e.target.value })
            }
          />
          <Input.Password
            placeholder="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Input.Password
            placeholder="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <Select
            placeholder="Select Role"
            style={{ width: 200 }}
            value={selectedRole}
            onChange={handleRoleChange}
            allowClear
          >
            <Option value="mother">Mother</Option>
            <Option value="trainer">Trainer</Option>
          </Select>
        </div>
      </Modal>
      <Modal
        title={"Edit User"}
        visible={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={handleEditUser}
        okText="Update"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Input
            placeholder="Enter Email"
            value={editUserData?.email}
            onChange={(e) =>
              setEditUserData({ ...editUserData, email: e.target.value })
            }
          />
        </div>
      </Modal>

      <Modal
        title={editProfileData.profileId ? "Edit Profile" : "Create Profile"}
        visible={isEditProfileModalVisible}
        onCancel={() => setIsEditProfileModalVisible(false)}
        onOk={handleUpdateProfile}
        okText={editProfileData.profileId ? "Update" : "Create"}
        okButtonProps={{ disabled: isUploadingImage }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Input
            placeholder="Name"
            value={editProfileData.name}
            onChange={(e) =>
              setEditProfileData({
                ...editProfileData,
                name: e.target.value,
              })
            }
          />
          <Input
            type="number"
            placeholder="Age"
            value={editProfileData.age}
            onChange={(e) =>
              setEditProfileData({ ...editProfileData, age: e.target.value })
            }
          />
          <Select
            value={editProfileData.gender}
            onChange={(value) =>
              setEditProfileData({ ...editProfileData, gender: value })
            }
            style={{ width: "100%" }}
          >
            <Option value="male">Male</Option>
            <Option value="female">Female</Option>
            <Option value="other">Other</Option>
          </Select>

          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {editProfileData.profilePicture && (
              <img
                src={editProfileData.profilePicture}
                alt="Preview"
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            )}
            <ImageUploader
              email={editUserData.email}
              onUploadStart={() => setIsUploadingImage(true)}
              onUploadSuccess={(key) => {
                setIsUploadingImage(false);
                setEditProfileData((prev) => ({
                  ...prev,
                  profilePicture: key,
                }));
              }}
              onUploadError={() => setIsUploadingImage(false)}
            />
          </div>
        </div>
      </Modal>

      {/* NEW: VERIFY TRAINER MODAL */}
      <Modal
        title={
          trainerToVerify?.isTrainerVerified
            ? "Unverify Trainer"
            : "Verify Trainer"
        }
        visible={isVerifyModalVisible}
        onCancel={() => setIsVerifyModalVisible(false)}
        onOk={handleConfirmVerification}
        okText={
          trainerToVerify?.isTrainerVerified
            ? "Confirm Unverify"
            : "Confirm Verification"
        }
      >
        {trainerToVerify && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <Paragraph>
              <strong>Name:</strong> {trainerToVerify.profile?.name || "N/A"}
            </Paragraph>
            <Paragraph>
              <strong>Email:</strong> {trainerToVerify.email}
            </Paragraph>
            <Paragraph>
              <strong>Expertise:</strong>{" "}
              {(() => {
                const combined = [
                  ...(Array.isArray(trainerToVerify.profile?.expertise)
                    ? trainerToVerify.profile.expertise
                    : []),
                  ...(Array.isArray(trainerToVerify.profile?.otherExpertise)
                    ? trainerToVerify.profile.otherExpertise
                    : []),
                ];
                return combined.length > 0 ? combined.join(", ") : "N/A";
              })()}
            </Paragraph>

            <Paragraph>
              <strong>Other Expertise:</strong>{" "}
              {Array.isArray(trainerToVerify.profile?.otherExpertise) &&
              trainerToVerify.profile?.otherExpertise.length > 0
                ? trainerToVerify.profile?.otherExpertise.join(", ")
                : "N/A"}
            </Paragraph>

            <div>
              <strong>Certificates:</strong>
              {Array.isArray(trainerToVerify.profile?.certificate) &&
              trainerToVerify.profile?.certificate.length > 0 ? (
                <ul>
                  {trainerToVerify.profile?.certificate.map((url, idx) => (
                    <li key={idx}>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        View Certificate {idx + 1}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>N/A</p>
              )}
            </div>

            <div>
              <strong>Taxation Forms:</strong>
              {Array.isArray(trainerToVerify.profile?.taxationForms) &&
              trainerToVerify.profile?.taxationForms.length > 0 ? (
                <ul>
                  {trainerToVerify.profile?.taxationForms.map((url, idx) => (
                    <li key={idx}>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        View Form {idx + 1}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>N/A</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Loader loading={isloading} />
    </div>
  );
};

export default TrainerList;
